import type { FileReference } from "../types/contracts";

import { log } from "./logger";

const markdownFileType = {
  description: "Markdown",
  accept: { "text/markdown": [".md", ".markdown", ".mdown"] },
};

function downloadFallback(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function openMarkdownFile(): Promise<{ content: string; file: FileReference } | null> {
  log("debug", "file.open.start", { hasPickerApi: Boolean(window.showOpenFilePicker) });
  if (window.showOpenFilePicker) {
    const [handle] = await window.showOpenFilePicker({
      multiple: false,
      types: [markdownFileType],
      excludeAcceptAllOption: false,
    });

    if (!handle) {
      log("info", "file.open.cancelled.picker");
      return null;
    }

    const file = await handle.getFile();
    const content = await file.text();
    return {
      content,
      file: { name: file.name, handle },
    };
  }

  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,.markdown,.mdown,text/markdown,text/plain";
    input.onchange = async () => {
      const [file] = Array.from(input.files ?? []);
      if (!file) {
        log("info", "file.open.cancelled.fallback");
        resolve(null);
        return;
      }

      const content = await file.text();
      log("info", "file.open.success.fallback", { filename: file.name, chars: content.length });
      resolve({ content, file: { name: file.name } });
    };
    input.click();
  });
}

export async function saveMarkdownFile(
  content: string,
  preferredName: string,
  currentFile?: FileReference,
): Promise<FileReference> {
  log("debug", "file.save.start", {
    hasSavePickerApi: Boolean(window.showSaveFilePicker),
    preferredName,
    currentFile: currentFile?.name,
  });
  if (window.showSaveFilePicker) {
    const handle = await window.showSaveFilePicker({
      suggestedName: currentFile?.name ?? preferredName,
      types: [markdownFileType],
      excludeAcceptAllOption: false,
    });

    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();

    log("info", "file.save.success.picker", { filename: handle.name, chars: content.length });
    return { name: handle.name, handle };
  }

  const fallbackName = currentFile?.name ?? preferredName;
  downloadFallback(fallbackName, content);
  log("warn", "file.save.fallback.download", { filename: fallbackName, chars: content.length });
  return { name: fallbackName };
}
